import { Button, Flex, Radio, Select, Typography } from "antd"
import { useTranslation } from "react-i18next"

import { type ZipLookupStatus } from "../../hooks/use-zip-lookup.ts"
import { type ZipMatch } from "./zip-lookup.ts"

interface ZipLookupHintProps {
  status: ZipLookupStatus
  onChoose: (match: ZipMatch) => void
  /** Keeps the city the user entered when the ZIP names another. */
  onDecline?: () => void
}

/**
 * Everything the ZIP lookup has to say, in the one place all four address forms
 * render it. Quiet by design: a line of text under the field, never a dialog,
 * and never a blocked save.
 */
const LABEL_ID = "zip-lookup-choice-label"

/** Up to five options are all shown, per UI_UX_GUIDELINES.md's numeric gate. The
 *  wire caps nothing, so a longer answer keeps the select. */
const RADIO_MAX = 5

/** More cities than this and the offer stops being a line under a field. */
const MISMATCH_MAX = 3

export function ZipLookupHint({
  status,
  onChoose,
  onDecline
}: ZipLookupHintProps) {
  const { t } = useTranslation()

  if (status.kind === "idle") return null

  if (status.kind === "cityMismatch") {
    const offered = status.matches.slice(0, MISMATCH_MAX)
    const question =
      offered.length === 1
        ? t("addressAutocomplete.zipCityMismatch", {
            zip: status.zip,
            city: offered[0].city
          })
        : t("addressAutocomplete.zipCityMismatchMany", {
            zip: status.zip,
            cities: offered.map((match) => match.city).join(", ")
          })

    return (
      <Flex vertical gap={2} role={"status"}>
        <Typography.Text type={"warning"} style={{ fontSize: 12 }}>
          {question}
        </Typography.Text>
        <Flex wrap gap={8}>
          {offered.map((match) => (
            <Button
              key={`${match.zip_code}-${match.city}`}
              type={"link"}
              size={"small"}
              className={"h-auto p-0 text-xs"}
              onClick={() => onChoose(match)}
            >
              {t("addressAutocomplete.zipCityUse", { city: match.city })}
            </Button>
          ))}
          {onDecline && (
            <Button
              type={"link"}
              size={"small"}
              className={"h-auto p-0 text-xs"}
              onClick={onDecline}
            >
              {t("addressAutocomplete.zipCityKeep", { city: status.city })}
            </Button>
          )}
        </Flex>
      </Flex>
    )
  }

  if (status.kind === "choices") {
    const options = status.matches.map((match, index) => ({
      value: index,
      label: `${match.city}, ${match.state}`
    }))

    return (
      <div className={"flex flex-col gap-1"}>
        <Typography.Text id={LABEL_ID} style={{ fontSize: 12 }}>
          {t("addressAutocomplete.zipChoose")}
        </Typography.Text>
        {options.length <= RADIO_MAX ? (
          <Radio.Group
            size={"small"}
            aria-labelledby={LABEL_ID}
            options={options}
            onChange={(event) => onChoose(status.matches[event.target.value])}
          />
        ) : (
          <Select
            size={"small"}
            className={"w-full"}
            aria-labelledby={LABEL_ID}
            options={options}
            onSelect={(index: number) => onChoose(status.matches[index])}
          />
        )}
      </div>
    )
  }

  const message =
    status.kind === "unknown"
      ? t("addressAutocomplete.zipUnknown")
      : status.kind === "failed"
        ? t("addressAutocomplete.zipLookupFailed")
        : t("addressAutocomplete.zipStateUnsupported", { state: status.state })

  return (
    <Typography.Text type={"secondary"} style={{ fontSize: 12 }}>
      {message}
    </Typography.Text>
  )
}
